/**
 * Created by neo on 22.04.22.
 */
import { makeAutoObservable } from 'mobx';
import { Audited, createAuditedFields } from '../../Audited';
import { ProgramParams, createProgramParams } from '../Program/ProgramParams';
import { ProgramSchedule, createProgramSchedule } from '../ProgramSchedule';
import { MindfulnessProgramPhase, createMindfulnessProgramPhase } from './MindfulnessProgramPhase';
import { HttpBackend } from '../../../Services/Http/HttpBackend';
import { MindfulnessProgramTemplate, MindfulnessProgramTemplateApi } from './Template/MindfulnessProgramTemplate';
import { Pageable } from '../../Interfaces/Pageable';

export type MindfulnessProgramQueryRequest = Pageable & {
  athleteId?: string;
  tags?: string[];
  excludedTags?: string[];
  active?: boolean;
};

export type MindfulnessProgram = Audited & {
  athleteId: string;
  type: string;
  programTemplateId: string;
  currentParams: ProgramParams;
  schedule: ProgramSchedule;
  currentPhase?: MindfulnessProgramPhase;
  startDateTime?: Date;
  endDateTime?: Date;
  template?: MindfulnessProgramTemplate;
  readonly currentWeekIndex: number | undefined;
  changeSchedule(schedule: ProgramSchedule): Promise<MindfulnessProgram>;
  end(): Promise<void>;
};

export function createMindfulnessProgram(json?: Partial<MindfulnessProgram>): MindfulnessProgram {
  const audited = createAuditedFields(json);
  const program: MindfulnessProgram = makeAutoObservable({
    ...audited,
    athleteId: json?.athleteId ?? '',
    type: json?.type ?? '',
    programTemplateId: json?.programTemplateId ?? '',
    currentParams: createProgramParams(json?.currentParams),
    schedule: createProgramSchedule(json?.schedule as any),
    currentPhase: json?.currentPhase ? createMindfulnessProgramPhase(json.currentPhase as any) : undefined,
    startDateTime: json?.startDateTime ? new Date(json.startDateTime as any) : undefined,
    endDateTime: json?.endDateTime ? new Date(json.endDateTime as any) : undefined,
    template: undefined as MindfulnessProgramTemplate | undefined,

    get currentWeekIndex(): number | undefined {
      if (this.template) {
        if (this.currentPhase?.phaseTemplateId) {
          const currentCycleIndex = this.template.phases.findIndex(
            (phase) => phase.id === this.currentPhase?.phaseTemplateId,
          );
          const startIndex = Math.max(
            0,
            this.currentPhase.weekIds.findIndex((id) => id === this.currentPhase?.currentWeekId),
          );

          return this.template.phases.reduce(
            (total, cycle, index) => (index < currentCycleIndex ? total + cycle.cycles : total),
            startIndex,
          );
        }
        return 0;
      }
      return undefined;
    },

    changeSchedule(schedule: ProgramSchedule): Promise<MindfulnessProgram> {
      this.schedule = schedule;
      return HttpBackend.post(`/coach-program/mindfulness/${this.id}/changeSchedule`, schedule).then(() => this);
    },

    end(): Promise<void> {
      return HttpBackend.delete(`/coach-program/mindfulness/${this.id}`);
    },
  });

  return program;
}

export const MindfulnessProgramApi = {
  find(params?: MindfulnessProgramQueryRequest): Promise<MindfulnessProgram[]> {
    return HttpBackend.get('/coach-program/mindfulness', params).then((result) =>
      (result ?? []).map((t: any) => createMindfulnessProgram(t)),
    );
  },

  currentlyActive(params?: MindfulnessProgramQueryRequest): Promise<MindfulnessProgram[]> {
    return HttpBackend.get('/coach-program/mindfulness/currentlyActive', params).then((result) =>
      (result ?? []).map((t: any) => createMindfulnessProgram(t)),
    );
  },

  get(id: string): Promise<MindfulnessProgram> {
    return HttpBackend.get(`/coach-program/mindfulness/${id}`).then((result) => createMindfulnessProgram(result));
  },
};
